import { useContext } from "react";
import { MobXProviderContext } from "mobx-react";

/**
 * App-specific stores available through the MobX `Provider`.
 *
 * This stays empty in the shared hook so each app can add its own store types
 * via declaration merging.
 */

// Must stay an interface so apps can extend it via declaration merging.
// eslint-disable-next-line @typescript-eslint/no-empty-interface, @typescript-eslint/consistent-type-definitions
export interface InjectableStores {}

/**
 * Custom hook to pull stores from the MobX Provider.
 */
// Overload: specific store names returns a Pick of the requested stores
export function useInject<
  StoreType extends object = InjectableStores,
  Key extends keyof StoreType = keyof StoreType,
>(...storeNames: Key[]): Pick<StoreType, Key>;

export function useInject<
  StoreType extends object = InjectableStores,
  Key extends keyof StoreType = keyof StoreType,
>(...storeNames: Key[]): Pick<StoreType, Key> {
  // Explicitly return Pick<StoreType, Key>
  const context = useContext(MobXProviderContext) as unknown as StoreType;

  if (!context) {
    throw new Error("useInject must be used within a MobX Provider component");
  }

  // If no store names provided (runtime check, though TS should prevent this if no-arg overload is gone)
  if (storeNames.length === 0) {
    // This case should ideally not be hit if the hook always requires arguments as per project standard.
    // Throwing an error makes this explicit.
    throw new Error("useInject was called with no store names, but arguments are required.");
  }

  // Otherwise pick only the requested stores
  const stores: Partial<Pick<StoreType, Key>> = {}; // More precise type for intermediate object
  storeNames.forEach((storeName) => {
    if (storeName in context) {
      stores[storeName] = context[storeName];
    } else {
      console.warn(`Store '${String(storeName)}' not found in Provider context.`);
    }
  });
  return stores as Pick<StoreType, Key>;
}
